import React from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  IconButton,
} from '@mui/material';
import type { SvgIconProps } from '@mui/material';
import { Close } from '@mui/icons-material';
import { motion, useReducedMotion, AnimatePresence } from 'framer-motion';
import type { FeatureTour } from '../../types';

interface SpotlightNotificationToastProps {
  tour: FeatureTour;
  onTakeTour: () => void;
  onSkip: () => void;
  onLater: () => void;
  onDismiss: () => void;
}

const MotionBox = motion.create(Box);

const SpotlightNotificationToast: React.FC<SpotlightNotificationToastProps> = ({
  tour,
  onTakeTour,
  onSkip,
  onLater,
  onDismiss,
}) => {
  const shouldReduceMotion = useReducedMotion();
  const Icon = tour.notification?.icon as React.ComponentType<SvgIconProps> | undefined;

  return (
    <AnimatePresence>
      <MotionBox
        initial={shouldReduceMotion ? false : { opacity: 0, y: 40, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={shouldReduceMotion ? undefined : { opacity: 0, y: 20, scale: 0.95, transition: { duration: 0.15 } }}
        transition={
          shouldReduceMotion
            ? { duration: 0 }
            : { type: 'spring', damping: 22, stiffness: 280, delay: 0.3 }
        }
        sx={{
          position: 'fixed',
          bottom: 88,
          right: 24,
          zIndex: 1600,
          maxWidth: 360,
          width: '100%',
          bgcolor: 'background.paper',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          border: 1,
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 2.5, pb: 1.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            {Icon && (
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: 'secondary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  mt: 0.25,
                }}
              >
                <Icon sx={{ fontSize: 20, color: '#fff' }} />
              </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
                {tour.notification?.title || tour.id}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                {tour.notification?.body}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={onDismiss}
              aria-label="Dismiss notification"
              sx={{
                mt: -0.5,
                mr: -0.5,
                color: 'text.secondary',
                '&:hover': { color: 'text.primary' },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ px: 2.5, pb: 2, pt: 0.5 }}>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              size="small"
              onClick={onTakeTour}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '8px',
                px: 2,
                bgcolor: 'secondary.main',
                '&:hover': { bgcolor: 'secondary.dark' },
              }}
            >
              Take a Tour
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={onSkip}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: '8px',
                px: 1.5,
                color: 'text.secondary',
                borderColor: 'divider',
                '&:hover': { borderColor: 'text.secondary' },
              }}
            >
              Skip
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={onLater}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: '8px',
                px: 1,
                minWidth: 'auto',
                color: 'text.secondary',
              }}
            >
              Later
            </Button>
          </Stack>
        </Box>
      </MotionBox>
    </AnimatePresence>
  );
};

export default SpotlightNotificationToast;
